import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

import { useGuidesStore } from "@/stores/useGuidesStore";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { useActAsStore } from "@/stores/act-as-store";
import {
  useAckAnnouncement,
  useUnreadAnnouncements,
} from "@/hooks/useAnnouncements";

import { AnnouncementDetailDialog } from "./AnnouncementDetailDialog";

/**
 * Front-and-center announcement modal. Renders the newest unread item from
 * `useUnreadAnnouncements()` while:
 *   - the user is on `/dashboard`, AND
 *   - none of the four mutex surfaces are active:
 *       * QuickStartTour (welcome modal or spotlight step),
 *       * Guides catalog drawer,
 *       * a walkthrough overlay,
 *       * admin act-as / impersonation.
 *
 * Backdrop / Escape closes without acknowledging — the item stays unread on
 * the server, but a session-local `closedIds` set prevents it from re-mounting
 * inside the same dashboard visit. The set is RESET when the user navigates
 * away from /dashboard and back, so unacked items re-fire on the next visit
 * (per spec § 6.5). The component is mounted globally in __root.tsx, so
 * "next visit" is detected via pathname transition rather than unmount.
 */
export function AnnouncementModal() {
  const { data: unread } = useUnreadAnnouncements();
  const ack = useAckAnnouncement();

  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  // canShow.announcement is false while the tour is active (see
  // useOnboardingController). catalogOpen + walkthroughOpen are observed
  // here for their own state — they also drive activeSurface in the
  // controller but suppression is duplicated here for the same effect.
  const canShow = useOnboardingStore((s) => s.canShow.announcement);
  const catalogOpen = useGuidesStore((s) => s.catalogOpen);
  const walkthroughOpen = useGuidesStore(
    (s) => s.openWalkthroughId !== null,
  );
  const isImpersonating = useActAsStore(
    (s) => s.actingAsUserId !== null,
  );

  const [closedIds, setClosedIds] = useState<Set<string>>(
    () => new Set(),
  );

  // Reset closedIds when the user leaves /dashboard so unacked items
  // re-fire on the next visit. Without this, the global mount in
  // __root.tsx would suppress the modal for the entire SPA session.
  const wasOnDashboard = useRef(pathname === "/dashboard");
  useEffect(() => {
    const onDashboard = pathname === "/dashboard";
    if (wasOnDashboard.current && !onDashboard) {
      // Just left the dashboard — clear so re-entry shows unacked items.
      setClosedIds(new Set());
    }
    wasOnDashboard.current = onDashboard;
  }, [pathname]);

  const top = unread?.find((a) => !closedIds.has(a.id)) ?? null;
  if (!top) return null;
  if (pathname !== "/dashboard") return null;
  if (
    !canShow ||
    catalogOpen ||
    walkthroughOpen ||
    isImpersonating
  )
    return null;

  return (
    <AnnouncementDetailDialog
      announcement={top}
      open={true}
      onClose={(acked) => {
        if (acked) {
          ack.mutate(top.id);
        } else {
          setClosedIds((prev) => new Set(prev).add(top.id));
        }
      }}
    />
  );
}
